<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import {
  buildMonthCells,
  monthYearLabel,
  parseIsoDate,
  shiftMonth,
  todayIsoDate,
  weekdayLabels,
} from './helpers';

const props = defineProps({
  modelValue: { type: String, default: null },
  accent: {
    type: String,
    default: 'blue',
    validator: value => ['blue', 'teal'].includes(value),
  },
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const initial = parseIsoDate(props.modelValue) || parseIsoDate(todayIsoDate());
const viewYear = ref(initial.year);
const viewMonth = ref(initial.month);

const syncViewToValue = iso => {
  const parsed = parseIsoDate(iso);
  if (!parsed) return;
  viewYear.value = parsed.year;
  viewMonth.value = parsed.month;
};

watch(
  () => props.modelValue,
  iso => syncViewToValue(iso)
);

const selectedClass = computed(() =>
  props.accent === 'teal'
    ? 'bg-n-teal-9 text-white font-bold'
    : 'bg-n-blue-9 text-white font-bold'
);

const todayRingClass = computed(() =>
  props.accent === 'teal'
    ? 'ring-1 ring-inset ring-n-teal-9'
    : 'ring-1 ring-inset ring-n-blue-9'
);

const monthLabel = computed(() =>
  monthYearLabel(viewYear.value, viewMonth.value, resolvedLocale.value)
);

const weekdays = computed(() => weekdayLabels(resolvedLocale.value));

const cells = computed(() => buildMonthCells(viewYear.value, viewMonth.value));

const cellSizeClass = computed(() => (props.compact ? 'size-6' : 'size-7'));

const cellClass = cell => {
  const selected = cell.iso === props.modelValue;
  // mx-auto + flex: sem isso o botão fica preso à esquerda da coluna do grid e o
  // anel do "hoje" aparece deslocado em relação ao número.
  const classes = [
    cellSizeClass.value,
    'mx-auto flex items-center justify-center rounded-full leading-none tabular-nums',
    props.compact ? 'text-[11px]' : 'text-[12px]',
  ];
  if (selected) classes.push(selectedClass.value);
  else if (cell.isToday) classes.push(todayRingClass.value, 'text-n-slate-12');
  else if (cell.outside) classes.push('text-n-slate-8');
  else classes.push('text-n-slate-12');
  if (!selected) classes.push('hover:bg-n-alpha-2');
  return classes;
};

const goMonth = delta => {
  const next = shiftMonth(viewYear.value, viewMonth.value, delta);
  viewYear.value = next.year;
  viewMonth.value = next.month;
};

const selectDate = iso => {
  emit('update:modelValue', iso);
  syncViewToValue(iso);
};

const clearDate = () => emit('update:modelValue', null);

const selectToday = () => {
  const today = todayIsoDate();
  syncViewToValue(today);
  emit('update:modelValue', today);
};
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-3">
    <div class="mb-2 flex items-center justify-between">
      <div class="text-sm font-bold text-n-slate-12">
        {{ monthLabel }}
      </div>
      <div class="flex items-center gap-0.5">
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-full hover:bg-n-alpha-2"
          :aria-label="t('DATE_PICKER.PREVIOUS_PERIOD')"
          @click="goMonth(-1)"
        >
          <span class="i-ri-arrow-left-s-line size-4" />
        </button>
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-full hover:bg-n-alpha-2"
          :aria-label="t('DATE_PICKER.NEXT_PERIOD')"
          @click="goMonth(1)"
        >
          <span class="i-ri-arrow-right-s-line size-4" />
        </button>
      </div>
    </div>

    <div class="grid grid-cols-7 gap-0.5 text-center">
      <div
        v-for="(label, index) in weekdays"
        :key="`wd-${index}`"
        class="text-[10.5px] font-bold text-n-slate-10"
      >
        {{ label }}
      </div>
      <button
        v-for="cell in cells"
        :key="cell.iso"
        type="button"
        :data-iso="cell.iso"
        :data-outside="cell.outside ? 'true' : 'false'"
        :aria-pressed="cell.iso === modelValue"
        :aria-current="cell.isToday ? 'date' : undefined"
        :class="cellClass(cell)"
        @click="selectDate(cell.iso)"
      >
        {{ cell.day }}
      </button>
    </div>

    <div class="mt-2 flex items-center justify-between">
      <button
        type="button"
        class="text-xs font-bold text-n-slate-11 hover:text-n-slate-12"
        @click="clearDate"
      >
        {{ t('DATE_PICKER.CLEAR') }}
      </button>
      <button
        type="button"
        class="text-xs font-bold text-n-blue-11"
        @click="selectToday"
      >
        {{ t('DATE_PICKER.TODAY') }}
      </button>
    </div>
  </div>
</template>
